import { baseApi } from '../../../shared/api/baseApi';
import type { ApiPath, Paginated } from '../../../shared/api/types';
import type {
    AdminOrganizationDto,
    AdminUserDto,
    CreateOrganizationBody,
    CreateUserBody,
    LoginAsResponse,
    OrganizationType,
    ReplaceOrganizationArgs,
    ScanLimitDto,
    UpdateScanLimitArgs,
    UpdateOrganizationBody,
    UpdateUserBody,
} from '../model/dto';

const USERS: ApiPath = '/api/admin/users';
const ORGANIZATIONS: ApiPath = '/api/admin/organizations';

/**
 * PATCH /api/admin/users.
 * send_email: true — бэкенд сохраняет новый пароль и отправляет письмо с логином и паролем.
 * Требует password в том же запросе.
 */
export type UpdateAdminUserArgs = UpdateUserBody & { send_email?: boolean };

/**
 * email_sent есть в ответе только при send_email: true.
 * false — пароль сохранён, но письмо не доставлено (изменения не откатываются).
 */
export type UpdateAdminUserResponse = AdminUserDto & { email_sent?: boolean };

export interface AdminListParams {
    page_num?: number;
    page_size?: number;
    query?: string;
    role?: string;
    organization_id?: string;
    status?: string;
}

function clean(params: AdminListParams): Record<string, string | number> {
    return Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== ''),
    ) as Record<string, string | number>;
}

export function toInt(value: string | number | undefined | null): number | undefined {
    if (value === undefined || value === null || value === '') return undefined;
    const digits = String(value).replace(/\D/g, '');
    if (digits === '') return undefined;
    const parsed = Number(digits);
    return Number.isSafeInteger(parsed) ? parsed : undefined;
}

function omitUndefined<T extends object>(body: T): T {
    return Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined)) as T;
}

type OrganizationPatchSource = {
    id: string;
    type: OrganizationType | null;
    inn?: string | null;
    address?: string | null;
    postalCode?: string | null;
};

export function buildUpdateOrganizationBody(
    current: OrganizationPatchSource,
    values: { name?: string; inn?: string; scan_limit_max?: number },
): UpdateOrganizationBody | null {
    if (current.type == null) return null;

    return omitUndefined<UpdateOrganizationBody>({
        organization_id: current.id,
        type: current.type,
        name: values.name || undefined,
        inn: toInt(values.inn ?? current.inn),
        address: current.address ?? undefined,
        postal_code: toInt(current.postalCode),
        scan_limit_max: values.scan_limit_max,
    });
}

export const accessApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getAdminUsers: builder.query<Paginated<AdminUserDto>, AdminListParams>({
            query: (params) => ({ url: USERS, params: clean(params) }),
            providesTags: ['AdminUsers'],
        }),

        createAdminUser: builder.mutation<AdminUserDto, CreateUserBody>({
            query: (body) => ({ url: USERS, method: 'POST', body }),
            invalidatesTags: ['AdminUsers'],
        }),

        sendUserLogin: builder.mutation<void, { email: string; password: string }>({
            query: (body) => ({
                url: `${USERS}/send_login` satisfies ApiPath,
                method: 'POST',
                body,
            }),
        }),

        updateAdminUser: builder.mutation<UpdateAdminUserResponse, UpdateAdminUserArgs>({
            query: (body) => ({ url: USERS, method: 'PATCH', body }),
            invalidatesTags: ['AdminUsers'],
        }),

        deleteAdminUser: builder.mutation<void, string>({
            query: (id) => ({ url: `${USERS}/${id}` satisfies ApiPath, method: 'DELETE' }),
            invalidatesTags: ['AdminUsers'],
        }),

        loginAsUser: builder.mutation<LoginAsResponse, string>({
            query: (userId) => ({ url: '/api/login/login_as', method: 'POST', body: { user_id: userId } }),
        }),

        getAdminOrganizations: builder.query<Paginated<AdminOrganizationDto>, AdminListParams>({
            query: (params) => ({ url: ORGANIZATIONS, params: clean(params) }),
            providesTags: ['AdminOrganizations'],
        }),

        createAdminOrganization: builder.mutation<AdminOrganizationDto, CreateOrganizationBody>({
            query: (body) => ({ url: ORGANIZATIONS, method: 'POST', body }),
            invalidatesTags: ['AdminOrganizations'],
        }),

        updateAdminOrganization: builder.mutation<AdminOrganizationDto, UpdateOrganizationBody>({
            query: (body) => ({
                url: `${ORGANIZATIONS}/update` satisfies ApiPath,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: ['AdminOrganizations'],
        }),

        updateOrganizationScanLimit: builder.mutation<ScanLimitDto, UpdateScanLimitArgs>({
            query: ({ organization_id, scan_limit_max }) => ({
                url: `${ORGANIZATIONS}/scan_limit/${organization_id}` satisfies ApiPath,
                method: 'PATCH',
                body: { scan_limit_max },
            }),
            invalidatesTags: ['AdminOrganizations'],
        }),

        replaceAdminOrganization: builder.mutation<AdminOrganizationDto, ReplaceOrganizationArgs>({
            query: ({ organization_id, body }) => ({
                url: `${ORGANIZATIONS}/${organization_id}` satisfies ApiPath,
                method: 'PUT',
                body: omitUndefined(body),
            }),
            invalidatesTags: ['AdminOrganizations'],
        }),

        deleteAdminOrganization: builder.mutation<void, string>({
            query: (id) => ({ url: `${ORGANIZATIONS}/${id}` satisfies ApiPath, method: 'DELETE' }),
            invalidatesTags: ['AdminOrganizations', 'AdminUsers'],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetAdminUsersQuery,
    useCreateAdminUserMutation,
    useUpdateAdminUserMutation,
    useDeleteAdminUserMutation,
    useLoginAsUserMutation,
    useGetAdminOrganizationsQuery,
    useCreateAdminOrganizationMutation,
    useUpdateAdminOrganizationMutation,
    useDeleteAdminOrganizationMutation,
    useSendUserLoginMutation,
    useReplaceAdminOrganizationMutation,
    useUpdateOrganizationScanLimitMutation,
} = accessApi;